import StorageService from '@/services/storage.service'
import storageKeys from '@/constants/storageKeys'
import { defaultFilters } from '@/models/PartnersSpends/helpers'
import partnersSpendsRepository from '@/repository/generatedRepository/partnersSpendsRepository'
import TableOptions from '@/models/TableOptions'
import { getSortedItems, prepareFilters } from '@/helpers/filters'
import sortBy from 'lodash/sortBy'
import { getPrepareData } from '@/helpers/functions'
import partnerSpendCustomPercentRepository from '@/repository/generatedRepository/partnerSpendCustomPercentRepository'
import partnerSpendUpdateBlockRepository from '@/repository/generatedRepository/partnerSpendUpdateBlockRepository'
import { formatNumber } from '@/helpers/number'

const state = {
  list: [],
  dates: {},
  filters: StorageService.get(storageKeys.PARTNERS_SPENDS_FILTERS) || { ...defaultFilters() },
  tableOptions: TableOptions.defaultTableOptions(),
}

const getters = {
  filters: state => state.filters,
  currentPartner: state => state.filters.partner,
  tableOptions: state => state.tableOptions,
  mainCheckboxDisabled: state => state.list.every(el => !el.hasData),
  list: state => state.list,
  listSpendRows: state =>
    state.list.filter(item => {
      return !Object.values(item.dates).every(date => !date?.base)
    }),
  listNoSpendRows: state =>
    state.list.filter(item => {
      return !Object.values(item.dates).every(date => !date?.has_data && !date?.base)
    }),
  listFiltered: (state, getters) => {
    const { showNoSpendRows } = state.filters.clientOptions
    const filteredList = [1, 3].includes(showNoSpendRows)
      ? state.list
      : showNoSpendRows === 2
        ? getters.listSpendRows
        : showNoSpendRows === 4
          ? state.list.filter(item => item.unsplit)
          : getters.listNoSpendRows
    return getSortedItems(filteredList, state.tableOptions)
  },
  dates: state => state.dates,
  total: (state, getters) => {
    const data = getters.listFiltered
    const total = data.reduce((acc, item) => {
      Object.entries(item.dates).forEach(([dateKey, dateVal]) => {
        acc[dateKey] = {
          base: (acc[dateKey]?.base || 0) + Number(dateVal ? dateVal.base : 0),
          sum: (acc[dateKey]?.sum || 0) + Number(dateVal ? dateVal.sum : 0),
        }
      })
      return acc
    }, {})
    total.total = Object.values(total).reduce(
      (acc, item) => {
        acc.base += item.base
        acc.sum += item.sum
        return acc
      },
      { base: 0, sum: 0 },
    )
    const totalRawAllDeposit = data.reduce((acc, item) => acc + (item._rawAllDeposit || 0), 0)
    const totalRawSum = data.reduce((acc, item) => acc + (item._rawSum || 0), 0)
    const totalRawBase = data.reduce((acc, item) => acc + (item._rawBase || 0), 0)
    const totalCommissionSum = data.reduce((acc, item) => acc + (item.total?.commission_sum || 0), 0)
    total.total.dss = totalRawSum > 0 ? (totalRawAllDeposit / totalRawSum) * 100 : 0
    total.total.recommended_spend = formatNumber(
      data.reduce((acc, item) => acc + (item.recommended_spend || 0), 0),
    )
    total.total.avg_commission = totalRawBase > 0 ? (totalCommissionSum / totalRawBase) * 100 : 0
    for (let key of Object.keys(total)) {
      total[key].base = formatNumber(total[key].base)
      total[key].sum = formatNumber(total[key].sum)
    }
    return total
  },
  isOpenFilterPanel: state =>
    state.filters.clientOptions && state.filters.clientOptions.filterPanelExpanded,
}

const actions = {
  async loadData(context, payload) {
    const { signal, isPartnerPercentUpdated = false, ...filters } = payload
    if (
      filters.partner &&
      filters.partner?.id === context.getters.filters.partner?.id &&
      isPartnerPercentUpdated
    ) {
      filters.partner.defaultPercent = context.getters.filters.partner.defaultPercent
    }
    context.commit('setFilters', filters)
    StorageService.set(storageKeys.PARTNERS_SPENDS_FILTERS, filters)
    const preparedFilters = prepareFilters({
      ...getPrepareData(filters, ['partner'], true),
      signal,
    })
    if (preparedFilters.shownNoSpendRows === 1) {
      preparedFilters.ftd = 1
    } else {
      delete preparedFilters.ftd
    }
    delete preparedFilters.shownNoSpendRows
    let data = []
    if (
      Array.isArray(preparedFilters.partner)
        ? !preparedFilters.partner.length
        : !preparedFilters.partner
    ) {
      context.commit('setList', { data, filters })
      return
    }
    data = (await partnersSpendsRepository.getTableData(preparedFilters)).data
    data = sortBy(data, ['stream', 'country'])
    data.forEach(row => { row.isBlocked = false })
    const groupIds = [...new Set(data.map(r => r.group_id).filter(Boolean))]
    if (groupIds.length && filters.date) {
      const blocksData = await context.dispatch('getSpendBlocks', {
        filter: { partnerGroup: { id: { value: groupIds.join('|'), operator: 'in' } } },
      })
      const blockItems = Array.isArray(blocksData) ? blocksData : blocksData?.items || []
      const viewedMonth = filters.date.substring(0, 7)
      const blockedGroupIdSet = new Set(
        blockItems
          .filter(b => (b.dateFrom || b.date || b.month || '').substring(0, 7) === viewedMonth)
          .map(b => (typeof b.partnerGroup === 'object' ? b.partnerGroup?.id : b.partnerGroup))
          .filter(id => id != null),
      )
      data.forEach(row => {
        row.isBlocked = blockedGroupIdSet.has(row.group_id)
      })
    }
    const res = await context.dispatch('recalcRowsTotal', data)
    data = res.data
    const dates = res.dates
    context.commit('setList', { data, filters, dates })
    return { products: data.items, pagination: data.pagination }
  },
  updatePercentAll(context, { percent, groupId }) {
    context.state.list.forEach(item => {
      if (item.group_id !== groupId) return
      const cells = [item, ...(item.segments || [])].flatMap(row => Object.values(row.dates))
      cells.forEach(date => {
        date.custom_percent = percent
        date.percent = percent
        date.sum = date.base ? (+date.base + date.base * (percent / 100)).toFixed(2) : 0
      })
    })
  },
  async updateTable(context, data) {
    await partnersSpendsRepository.updateTableData(data)
  },
  async createCustomPercent(context, data) {
    await partnerSpendCustomPercentRepository.post(data)
  },
  async updateCustomPercent(context, payload) {
    const { id, ...data } = payload
    await partnerSpendCustomPercentRepository.put(id, data)
  },
  async updateSpendByPartner(context, data) {
    await partnersSpendsRepository.updateSpendByPartner(data)
  },
  async updateSpendByStreamAndCountry(context, data) {
    await partnersSpendsRepository.updateSpendByStreamAndCountry(data)
  },
  filtersStorageReset() {
    StorageService.remove(storageKeys.PARTNERS_SPENDS_FILTERS)
  },
  recalcRowsTotal(context, data) {
    const dates = {}
    const calcRowTotals = el => {
      el.rowChecked = false
      el.isBlocked = el.isBlocked || false
      el.hasData = Object.values(el.dates).some(date => date?.base)
      el.dates = Object.entries(el.dates).reduce((acc, [date, value]) => {
        if (value === null) {
          acc[date] = {
            base: 0,
            custom_percent: null,
            custom_percent_id: null,
            has_data: false,
            percent: 0,
            sum: 0,
            previous_percent: null,
            updated_at: null,
            updated_by: null,
            all_deposit_amount: 0,
            new_deposit_amount: 0,
          }
        } else {
          acc[date] = value
        }
        return acc
      }, {})
      el.total = Object.entries(el.dates).reduce(
        (acc, [date, item]) => {
          acc.base += item ? +item.base : 0
          acc.sum += item ? +item.sum : 0
          acc.all_deposit_amount += +(item?.all_deposit_amount ?? 0)
          acc.new_deposit_amount += +(item?.new_deposit_amount ?? 0)
          if (item && +item.base > 0) acc.commission_sum += (+item.base * +item.percent) / 100
          item.checked = false
          item.percent =
            !item.percent && item.custom_percent ? item.custom_percent : item.percent || 0
          if (!dates[date]?.active) {
            dates[date] = {
              active: !!item.base,
              colChecked: false,
            }
          }
          return acc
        },
        {
          base: 0,
          sum: 0,
          all_deposit_amount: 0,
          new_deposit_amount: 0,
          commission_sum: 0,
        },
      )
      const rawBase = el.total.base
      const rawSum = el.total.sum
      const rawAllDeposit = el.total.all_deposit_amount
      const rawNewDeposit = el.total.new_deposit_amount
      const avgCommission = rawBase > 0 ? el.total.commission_sum / rawBase : 0
      el.dss = rawSum > 0 ? (rawAllDeposit / rawSum) * 100 : 0
      el.recommended_spend = rawNewDeposit * (el.cfd || 0)
      el.avg_commission = avgCommission * 100
      el._rawBase = rawBase
      el._rawAllDeposit = rawAllDeposit
      el._rawSum = rawSum
      el.total.base = formatNumber(rawBase)
      el.total.sum = formatNumber(rawSum)
      el.total.all_deposit_amount = formatNumber(rawAllDeposit)
      el.total.new_deposit_amount = formatNumber(rawNewDeposit)
      return el
    }
    // order назначает buildVisibleRows по видимому списку (пары + раскрытые сегменты)
    const preparedData = data.map(el => {
      calcRowTotals(el)
      if (el.order === undefined) el.order = 0
      el.segments = (el.segments || []).map(segment => {
        segment._isSegment = true
        segment.stream_id = el.stream_id
        segment.country_id = el.country_id
        segment.group_id = el.group_id
        segment.cfd = el.cfd
        segment.isBlocked = el.isBlocked
        if (segment.order === undefined) segment.order = 0
        return calcRowTotals(segment)
      })
      return el
    })
    context.commit('updateList', preparedData)
    return {
      data: preparedData,
      dates,
    }
  },
  updateFiltersClientOptions({ commit, getters }, clientOptions) {
    const updatedFilters = {
      ...getters.filters,
      clientOptions,
    }
    commit('setFilters', updatedFilters)
    StorageService.set(storageKeys.PARTNERS_SPENDS_FILTERS, updatedFilters)
  },
  updateFilters({ commit, getters }, filters) {
    const updatedFilters = {
      ...getters.filters,
      ...filters,
    }
    commit('setFilters', updatedFilters)
    StorageService.set(storageKeys.PARTNERS_SPENDS_FILTERS, updatedFilters)
  },
  async getSpendBlocks(context, params) {
    const { data } = await partnerSpendUpdateBlockRepository.list(params)
    return data
  },
  async createSpendBlock(context, data) {
    return partnerSpendUpdateBlockRepository.post(data)
  },
  async deleteSpendBlock(context, id) {
    return partnerSpendUpdateBlockRepository.delete(id)
  },
  updateSort(context, filters) {
    context.commit('setFilters', filters)
    StorageService.set(storageKeys.PARTNERS_SPENDS_FILTERS, filters)
    context.commit('setSort', {
      sortBy: filters.sortBy,
      sortDesc: filters.sortDesc,
    })
  },
}

const mutations = {
  setList(state, { data, filters, dates = {} }) {
    state.list = data
    state.dates = dates
    state.tableOptions = new TableOptions(data.pagination, filters)
  },
  updateList(state, list = []) {
    state.list = list
  },
  setFilters(state, filters) {
    state.filters = { ...filters }
  },
  setSort(state, { sortBy, sortDesc }) {
    state.tableOptions = {
      ...state.tableOptions,
      sortBy,
      sortDesc,
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
